export interface PerformanceMetrics {
  cacheHitRate: number;
  averageResponseTime: number;
  apiFailureRate: number;
  fallbackUsageRate: number;
  dataFreshness: number;
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  apiFailures: number;
  fallbackUsages: number;
}

export interface RequestMetric {
  timestamp: number;
  queryKey: string;
  responseTime: number;
  source: 'api' | 'cache' | 'fallback';
  success: boolean;
  cacheAge?: number;
  endpoint?: string;
}

export class PerformanceMonitor {
  private metrics: RequestMetric[] = [];
  private readonly maxMetrics = 1000; // Keep last 1000 requests
  private startTime = Date.now();

  public recordRequest(metric: RequestMetric): void {
    this.metrics.push(metric);
    
    // Keep only the most recent metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  public getMetrics(timeWindow?: number): PerformanceMetrics {
    const cutoff = timeWindow ? Date.now() - timeWindow : 0;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    if (relevantMetrics.length === 0) {
      return this.getEmptyMetrics();
    }

    const totalRequests = relevantMetrics.length;
    const cacheHits = relevantMetrics.filter(m => m.source === 'cache').length;
    const cacheMisses = relevantMetrics.filter(m => m.source === 'api').length;
    const apiFailures = relevantMetrics.filter(m => !m.success && m.source === 'api').length;
    const fallbackUsages = relevantMetrics.filter(m => m.source === 'fallback').length;

    const totalResponseTime = relevantMetrics.reduce((sum, m) => sum + m.responseTime, 0);
    const averageResponseTime = totalResponseTime / totalRequests;

    const cacheHitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0;
    const apiFailureRate = cacheMisses > 0 ? (apiFailures / cacheMisses) * 100 : 0;
    const fallbackUsageRate = totalRequests > 0 ? (fallbackUsages / totalRequests) * 100 : 0;

    // Calculate data freshness (average age of cached data)
    const cachedMetrics = relevantMetrics.filter(m => m.source === 'cache' && m.cacheAge !== undefined);
    const totalCacheAge = cachedMetrics.reduce((sum, m) => sum + (m.cacheAge || 0), 0);
    const dataFreshness = cachedMetrics.length > 0 ? totalCacheAge / cachedMetrics.length : 0;

    return {
      cacheHitRate,
      averageResponseTime,
      apiFailureRate,
      fallbackUsageRate,
      dataFreshness,
      totalRequests,
      cacheHits,
      cacheMisses,
      apiFailures,
      fallbackUsages,
    };
  }

  public getMetricsByEndpoint(timeWindow?: number): Record<string, PerformanceMetrics> {
    const cutoff = timeWindow ? Date.now() - timeWindow : 0;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    const endpointGroups = relevantMetrics.reduce((groups, metric) => {
      const endpoint = metric.endpoint || 'unknown';
      if (!groups[endpoint]) {
        groups[endpoint] = [];
      }
      groups[endpoint].push(metric);
      return groups;
    }, {} as Record<string, RequestMetric[]>);

    const result: Record<string, PerformanceMetrics> = {};
    
    Object.entries(endpointGroups).forEach(([endpoint, metrics]) => {
      result[endpoint] = this.calculateMetricsForGroup(metrics);
    });

    return result;
  }

  public getTopSlowQueries(limit: number = 10, timeWindow?: number): Array<{
    queryKey: string;
    averageResponseTime: number;
    requestCount: number;
  }> {
    const cutoff = timeWindow ? Date.now() - timeWindow : 0;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    const queryGroups = relevantMetrics.reduce((groups, metric) => {
      if (!groups[metric.queryKey]) {
        groups[metric.queryKey] = [];
      }
      groups[metric.queryKey].push(metric);
      return groups;
    }, {} as Record<string, RequestMetric[]>);

    return Object.entries(queryGroups)
      .map(([queryKey, metrics]) => ({
        queryKey,
        averageResponseTime: metrics.reduce((sum, m) => sum + m.responseTime, 0) / metrics.length,
        requestCount: metrics.length,
      }))
      .sort((a, b) => b.averageResponseTime - a.averageResponseTime)
      .slice(0, limit);
  }

  public getCacheEfficiencyReport(): {
    overallEfficiency: number;
    recommendations: string[];
    criticalIssues: string[];
  } {
    const metrics = this.getMetrics();
    const recommendations: string[] = [];
    const criticalIssues: string[] = [];

    // Calculate overall efficiency score (0-100)
    let efficiencyScore = 0;

    // Cache hit rate contributes 40% to efficiency
    efficiencyScore += (metrics.cacheHitRate / 100) * 40;

    // Low failure rate contributes 30% to efficiency
    efficiencyScore += ((100 - metrics.apiFailureRate) / 100) * 30;

    // Fast response time contributes 20% to efficiency
    const responseTimeScore = Math.max(0, (5000 - metrics.averageResponseTime) / 5000);
    efficiencyScore += responseTimeScore * 20;

    // Data freshness contributes 10% to efficiency
    const freshnessScore = Math.max(0, (24 * 60 * 60 * 1000 - metrics.dataFreshness) / (24 * 60 * 60 * 1000));
    efficiencyScore += freshnessScore * 10;

    // Generate recommendations
    if (metrics.cacheHitRate < 50) {
      recommendations.push('Consider increasing cache duration or implementing more aggressive prefetching');
    }

    if (metrics.apiFailureRate > 10) {
      criticalIssues.push('High API failure rate detected - investigate network or server issues');
    }

    if (metrics.averageResponseTime > 2000) {
      recommendations.push('Average response time is high - consider optimizing queries or increasing cache usage');
    }

    if (metrics.fallbackUsageRate > 20) {
      criticalIssues.push('High fallback usage indicates frequent API unavailability');
    }

    if (metrics.dataFreshness > 60 * 60 * 1000) { // 1 hour
      recommendations.push('Data is often stale - consider reducing cache duration or implementing background refresh');
    }

    return {
      overallEfficiency: Math.round(efficiencyScore),
      recommendations,
      criticalIssues,
    };
  }

  public exportMetrics(): string {
    return JSON.stringify({
      exportTime: new Date().toISOString(),
      monitoringDuration: Date.now() - this.startTime,
      metrics: this.getMetrics(),
      endpointMetrics: this.getMetricsByEndpoint(),
      topSlowQueries: this.getTopSlowQueries(),
      efficiencyReport: this.getCacheEfficiencyReport(),
    }, null, 2);
  }

  public clearMetrics(): void {
    this.metrics = [];
    this.startTime = Date.now();
  }

  private calculateMetricsForGroup(metrics: RequestMetric[]): PerformanceMetrics {
    if (metrics.length === 0) {
      return this.getEmptyMetrics();
    }

    const totalRequests = metrics.length;
    const cacheHits = metrics.filter(m => m.source === 'cache').length;
    const cacheMisses = metrics.filter(m => m.source === 'api').length;
    const apiFailures = metrics.filter(m => !m.success && m.source === 'api').length;
    const fallbackUsages = metrics.filter(m => m.source === 'fallback').length;

    const totalResponseTime = metrics.reduce((sum, m) => sum + m.responseTime, 0);
    const averageResponseTime = totalResponseTime / totalRequests;

    const cacheHitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0;
    const apiFailureRate = cacheMisses > 0 ? (apiFailures / cacheMisses) * 100 : 0;
    const fallbackUsageRate = totalRequests > 0 ? (fallbackUsages / totalRequests) * 100 : 0;

    const cachedMetrics = metrics.filter(m => m.source === 'cache' && m.cacheAge !== undefined);
    const totalCacheAge = cachedMetrics.reduce((sum, m) => sum + (m.cacheAge || 0), 0);
    const dataFreshness = cachedMetrics.length > 0 ? totalCacheAge / cachedMetrics.length : 0;

    return {
      cacheHitRate,
      averageResponseTime,
      apiFailureRate,
      fallbackUsageRate,
      dataFreshness,
      totalRequests,
      cacheHits,
      cacheMisses,
      apiFailures,
      fallbackUsages,
    };
  }

  private getEmptyMetrics(): PerformanceMetrics {
    return {
      cacheHitRate: 0,
      averageResponseTime: 0,
      apiFailureRate: 0,
      fallbackUsageRate: 0,
      dataFreshness: 0,
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiFailures: 0,
      fallbackUsages: 0,
    };
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();